import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Database, ChevronDown, Download, RefreshCw, Loader2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import type { Site } from '../types';
import { callSiteApi } from '../utils';

interface Backup {
  id: string;
  name: string;
  lastModified: string;
  type: 'full' | 'incremental' | 'legacy';
  totalSize: number;
  files?: { key: string; size: number } | null;
  database?: { key: string; size: number } | null;
  legacy?: { key: string; size: number } | null;
}

interface BackupsWidgetProps {
  site: Site;
}

export const BackupsWidget: React.FC<BackupsWidgetProps> = ({ site }) => {
  const { toast } = useToast();
  const [backups, setBackups] = useState<Backup[]>([]);
  const [loading, setLoading] = useState(true);
  const [isEventsExpanded, setIsEventsExpanded] = useState(true);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [restoring, setRestoring] = useState<string | null>(null);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'LIST_BACKUPS', siteId: site.id }
      });
      if (error) throw error;
      setBackups(data.backups || []);
    } catch (err: any) {
      console.error('Failed to fetch backups:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBackups();
    // Listen for backup status updates
    const channel = supabase
      .channel(`site-backups-widget-${site.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workspace_sites',
          filter: `id=eq.${site.id}`,
        },
        (payload) => {
          const { backup_status } = payload.new;
          if (backup_status === 'idle') {
            fetchBackups();
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [site.id]);

  const handleDownload = async (backupKey: string, fileName: string) => {
    try {
      setDownloading(backupKey);
      const { data, error } = await supabase.functions.invoke('r2-backups', {
        body: { action: 'GET_DOWNLOAD_URL', key: backupKey, siteId: site.id }
      });
      if (error) throw error;
      if (!data.url) throw new Error('Failed to get download URL');
      const link = document.createElement('a');
      link.href = data.url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({ title: 'Download Started' });
    } catch (err: any) {
      toast({ title: 'Download Failed', description: err.message, variant: 'destructive' });
    } finally {
      setDownloading(null);
    }
  };

  const getKeysToRestore = (backup: Backup, scope: 'full' | 'files' | 'database') => {
    const keys: any[] = [];
    if (backup.type === 'incremental' && (scope === 'full' || scope === 'files')) {
      const baseFull = backups.find(b => b.type === 'full' && b.id < backup.id);
      if (baseFull?.files) keys.push({ key: baseFull.files.key, scope: 'files' });
      else if (baseFull?.legacy) keys.push({ key: baseFull.legacy.key, scope: 'files' });
    }
    if (scope === 'full') {
      if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'full' });
      if (backup.files) keys.push({ key: backup.files.key, scope: 'full' });
      if (backup.database) keys.push({ key: backup.database.key, scope: 'full' });
    } else if (scope === 'files') {
      if (backup.files) keys.push({ key: backup.files.key, scope: 'files' });
      else if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'files' });
    } else if (scope === 'database') {
      if (backup.database) keys.push({ key: backup.database.key, scope: 'database' });
      else if (backup.legacy) keys.push({ key: backup.legacy.key, scope: 'database' });
    }
    return keys;
  };

  const handleRestore = async (keysToRestore: any[]) => {
    if (!keysToRestore || keysToRestore.length === 0) return;
    if (!window.confirm('Are you sure you want to restore from this backup? This will overwrite existing files/database based on your selection.')) return;
    try {
      setRestoring(keysToRestore[0]?.key);
      await callSiteApi(site.url, site.publicKey, site.secretKey, 'backups/restore', 'POST', JSON.stringify({ keys_to_restore: keysToRestore }));
      toast({ title: 'Restoration Started', description: 'Site restoration is now running in the background.' });
    } catch (err: any) {
      toast({ title: 'Restoration Failed', description: err.message, variant: 'destructive' });
    } finally {
      setRestoring(null);
    }
  };

  const latestBackups = backups.slice(0, 3);
  const isEnabled = site.backupEnabled;

  return (
    <div className="bg-card border border-border rounded-xl px-5 pt-5 pb-4 shadow-sm w-full h-full flex flex-col">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-base font-extrabold text-foreground tracking-tight">Backups</h3>
      </div>

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full border-2 flex items-center justify-center transition-colors ${!isEnabled ? 'border-muted text-muted-foreground' : 'border-workspace-accent text-workspace-accent'}`}>
            <Database className={`w-5 h-5 ${!isEnabled ? 'opacity-50' : ''}`} />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-bold text-foreground leading-none">
              {loading ? 'Loading...' : `${backups.length} Total Backups`}
            </span>
            {isEnabled && site.backupLastRun && (
              <span className="text-[11px] font-medium text-muted-foreground mt-1.5 opacity-70">
                Last run: {new Date(site.backupLastRun).toLocaleDateString()}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2.5">
          <span className={`text-[11px] font-black ${!isEnabled ? 'text-muted-foreground' : 'text-workspace-accent'}`}>
            {isEnabled ? 'ON' : 'OFF'}
          </span>
          <div className={`w-9 h-5 rounded-full relative shadow-inner transition-all flex items-center px-1 ${!isEnabled ? 'bg-muted' : 'bg-workspace-accent'}`}>
            <div className={`w-3 h-3 bg-white rounded-full shadow-sm transform transition-transform ${isEnabled ? 'translate-x-4' : 'translate-x-0'}`} />
          </div>
        </div>
      </div>

      <div className="mt-5 pt-4 border-t border-border/50 flex-1">
        <div className="flex items-center justify-between mb-3 group cursor-pointer" onClick={() => setIsEventsExpanded(!isEventsExpanded)}>
          <h4 className="text-xs font-bold text-muted-foreground uppercase tracking-widest">Latest backups</h4>
          <ChevronDown className={`w-3.5 h-3.5 text-muted-foreground transition-transform duration-200 ${isEventsExpanded ? 'rotate-180' : ''}`} />
        </div>

        <AnimatePresence>
          {isEventsExpanded && (
            <motion.div
              key="latest-backups"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="overflow-hidden flex flex-col gap-3 h-full"
            >
              <div className="flex-1 flex flex-col gap-3">
                {loading ? (
                  <p className="text-xs text-muted-foreground italic">Loading backups...</p>
                ) : latestBackups.length === 0 ? (
                  <p className="text-xs text-muted-foreground italic">No backups available</p>
                ) : (
                  latestBackups.map(backup => {
                    const dateObj = new Date(backup.lastModified);
                    const dateStr = dateObj.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                    
                    return (
                      <div key={backup.id} className="flex items-center justify-between group/bkp">
                        <div className="flex items-center gap-2 overflow-hidden mr-2">
                          <Database className="w-3.5 h-3.5 text-workspace-accent shrink-0" />
                          <div className="text-xs font-medium text-foreground/80 dark:text-foreground/70 truncate">
                            {backup.name} <span className="text-muted-foreground/60 mx-1">•</span> {dateStr}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {backup.files && backup.database ? (
                            <>
                              <button onClick={() => {
                                handleDownload(backup.files!.key, backup.name + '-files.zip');
                                setTimeout(() => handleDownload(backup.database!.key, backup.name + '-database.zip'), 500);
                              }} disabled={!!downloading} className="p-1 rounded text-blue-500 hover:bg-blue-500/10 transition-colors disabled:opacity-50" title="Download Full">
                                {downloading === backup.files?.key || downloading === backup.database?.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                              </button>
                              <button onClick={() => handleRestore(getKeysToRestore(backup, 'full'))} disabled={!!restoring} className="p-1 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors disabled:opacity-50" title="Restore Full">
                                {restoring === (backup.files?.key || backup.legacy?.key) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                              </button>
                            </>
                          ) : (
                            <>
                              {(backup.legacy || backup.files || backup.database) && (
                                <button onClick={() => handleDownload((backup.legacy || backup.files || backup.database)!.key, backup.name + '.zip')} disabled={!!downloading} className="p-1 rounded text-blue-500 hover:bg-blue-500/10 transition-colors disabled:opacity-50" title="Download">
                                  {downloading === (backup.legacy || backup.files || backup.database)?.key ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
                                </button>
                              )}
                              {(backup.legacy || backup.files || backup.database) && (
                                <button onClick={() => handleRestore(getKeysToRestore(backup, backup.files ? 'files' : backup.database ? 'database' : 'full'))} disabled={!!restoring} className="p-1 rounded text-emerald-500 hover:bg-emerald-500/10 transition-colors disabled:opacity-50" title="Restore">
                                  {restoring === (backup.files?.key || backup.database?.key || backup.legacy?.key) ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                </button>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};
